import type { BackOfficeCatalog } from './es'

export const en = {
  'app.name': 'Eklevo',
  'app.panel': 'Admin panel',
  'app.title': 'Eklevo · Admin panel',
  'common.language': 'Language',
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.filters.clear': 'Clear filters',
  'common.error.organizationChanged':
    'You have opened another company in another tab, and the panel works with one at a time. Open this one again to continue.',
  'common.signOut': 'Sign out',
  'common.error.generic': 'Something went wrong. Please try again in a few minutes.',
  'common.error.rateLimited': 'Too many requests in a row. Wait a minute and try again.',
  'common.notFound.title': 'Page not found',
  'common.notFound.body': 'The page you are looking for does not exist.',
  'common.notFound.action': 'Go to the home page',
  'footer.rights': '© {year} Eklevo. For the Eklevo team only.',
  'footer.help': 'Help',

  'signIn.title': 'Sign in to the admin panel',
  'signIn.intro':
    'For the Eklevo team only. We will email you a link and then ask for your passkey.',
  'signIn.emailLabel': 'Eklevo email',
  'signIn.submit': 'Email me a sign-in link',
  'signIn.sending': 'Sending…',
  'signIn.sent':
    'If the address belongs to someone on the Eklevo team, you will receive a link to sign in. It expires in 15 minutes and can only be used once.',
  'signIn.invalidEmail': 'Enter a valid email address.',
  'signIn.confirm.body':
    'Press the button to continue with this link. We will then ask for your passkey.',
  'signIn.confirm.submit': 'Continue',
  'signIn.confirm.busy': 'Checking the link…',
  'signIn.linkInvalid.title': 'The link has expired or has already been used',
  'signIn.linkInvalid.body': 'Ask for a new link to sign in.',
  'signIn.linkInvalid.action': 'Ask for a new link',
  'signIn.ended.idle':
    'Your session closed after 30 minutes without activity. Sign in again to continue.',
  'signIn.ended.expired': 'Your session reached its maximum of 8 hours. Sign in again to continue.',
  'signIn.ended.closed':
    'Your session was closed: you signed in on another device, or it was closed from the operator tool. Sign in again to continue.',
  'signIn.ended.signedOut': 'You have signed out.',

  'passkey.title': 'Confirm it is you',
  'passkey.body': 'Use your passkey: your device’s fingerprint, face or PIN.',
  'passkey.submit': 'Use my passkey',
  'passkey.busy': 'Waiting for your passkey…',
  'passkey.lost.summary': 'Lost your passkey?',
  'passkey.lost.body':
    'Ask Eklevo’s technical lead for a new registration permission: it deletes your previous passkeys and lets you register a new one within the hour. There is no other way in.',
  'enroll.title': 'Register your passkey',
  'enroll.body':
    'To sign in to the panel you need a passkey. It unlocks with your device’s fingerprint, face or PIN, and Eklevo stores no password: only the public part of the key.',
  'enroll.hint':
    'We recommend two: one on your computer and one on your phone. You can add the second once you are in.',
  'enroll.submit': 'Create my passkey',
  'enroll.busy': 'Follow the instructions on your device…',
  'enroll.done.title': 'Passkey registered',
  'enroll.done.body': 'Now use it to sign in.',
  'enroll.done.submit': 'Sign in with my passkey',
  'enrollmentRequired.title': 'You have no passkey yet',
  'enrollmentRequired.body':
    'To register your first one you need a permission from the operator tool, valid for one hour. Ask Eklevo’s technical lead for it and, once you have it, press “I have the permission”.',
  'enrollmentRequired.check': 'I have the permission',
  'enrollmentRequired.stillMissing': 'You have no open registration permission yet.',

  'webauthn.cancelled': 'No passkey was used: it was cancelled or took too long. Try again.',
  'webauthn.alreadyRegistered':
    'This device already has a passkey of yours. Use that passkey, or create the new one on another device.',
  'webauthn.unsupported':
    'This browser cannot use passkeys. Use a recent version of Chrome, Edge, Firefox or Safari.',
  'webauthn.noUserVerification':
    'This device cannot check it is you with a fingerprint, face or PIN. Turn on the screen lock or use another device.',
  'webauthn.wrongAddress':
    'Passkeys cannot be used from this address. Open the panel from the address in the email’s link.',
  'webauthn.notRecognized':
    'We did not recognise this passkey. Try another of your passkeys; if you have none, ask for a new registration permission.',
  'webauthn.tooManyAttempts':
    'There have been too many failed attempts in the last hour. Wait a while or ask Eklevo’s technical lead for help.',
  'webauthn.expired': 'The request has expired. Try again.',
  'webauthn.enrollmentNotAllowed':
    'You no longer have an open registration permission: it expired or has been used. Ask for a new one.',
  'webauthn.tooManyPasskeys': 'You already have the maximum of 3 passkeys.',
  'webauthn.failed': 'The passkey could not be used. Try again.',

  'shell.skip': 'Skip to content',
  'nav.label': 'Panel sections',
  'nav.home': 'Home',
  'nav.companies': 'Companies',
  'nav.catalog': 'Courses',
  'status.organization.active': 'Active',
  'status.organization.suspended': 'Suspended',
  'status.program.not_launched': 'Not launched',
  'status.program.active': 'Active',
  'status.program.stopped': 'Stopped',
  'status.program.completed': 'Completed',
  'status.course.draft': 'Draft',
  'status.course.published': 'Published',
  'status.course.archived': 'Archived',
  'home.title': 'Home',
  'home.body':
    "Eklevo's companies and its course catalogue, in counts, and the team's activity. No person of the companies.",
  'home.stats.label': 'Indicators',
  'home.stats.organizations': 'Active companies',
  'home.stats.people': 'Active people',
  'home.stats.peopleHint': 'Of the active companies',
  'home.stats.courses': 'Published courses',
  'home.stats.assignments': 'Assignments in progress',
  'home.stats.assignmentsHint': "Not finished, of the active companies' people",
  'home.stats.completion': 'Overall completion',
  'home.stats.completionHint': '{count, number} of {total, number} assignments finished',
  'home.stats.completionNone': 'No assignments yet',
  'home.stats.noValue': '—',
  'home.empty.title': 'There are no active companies yet',
  'home.empty.body':
    'Add the first one with the wizard: its details, its HR team, its information notice and its courses.',
  'home.empty.add': 'Add a new company',
  'home.go.label': 'Shortcuts',
  'home.go.companies':
    'Adding a company, editing it, its HR team, and suspending or reactivating it.',
  'home.go.catalog': "Eklevo's catalogue, and activating a course for a company.",
  'home.access.title': 'Your access',
  'home.session.title': 'Your session',
  'home.session.endsAt':
    'It ends at {endsAt, time, short} at the latest, and earlier after 30 minutes without activity.',
  'home.passkeys.title': 'Your passkeys',
  'home.passkeys.count':
    '{count, plural, one {You have # passkey, out of a maximum of 3.} other {You have # passkeys, out of a maximum of 3.}}',
  'home.passkeys.hint':
    'We recommend two: one on your computer and one on your phone. If you lose a device, tell Eklevo’s technical lead.',
  'home.passkeys.add': 'Add a passkey',
  'home.passkeys.createTitle': 'Create the new passkey',
  'home.passkeys.createBody':
    'Create it on this device or, with the QR code your browser shows, on your phone.',
  'home.passkeys.create': 'Create the new passkey',
  'home.passkeys.added':
    '{count, plural, one {Passkey added. You now have # passkey.} other {Passkey added. You now have # passkeys.}}',
  'home.passkeys.full': 'You already have the maximum of 3 passkeys.',
  'home.activity.title': "Recent activity of Eklevo's team",
  'home.activity.body':
    "The team's latest actions, in the panel and with the operator tool. Never those of the companies' people.",
  'home.activity.empty': 'No activity of the team yet.',
  'home.activity.someone': 'Someone of the team',
  'home.activity.at': '{at, date, medium}, {at, time, short}',
  'home.activity.organization.created': 'added {company}.',
  'home.activity.organization.updated': "edited {company}'s details.",
  'home.activity.organization.brand_set': "changed {company}'s brand.",
  'home.activity.organization.suspended': 'suspended {company}.',
  'home.activity.organization.reactivated': 'reactivated {company}.',
  'home.activity.program.activated': 'activated «{course}» for {company}.',
  'home.activity.person.invited': "sent an invitation of {company}'s HR team again.",
  'home.activity.course.created': 'created the course «{course}».',
  'home.activity.course.published': 'published «{course}».',
  'home.activity.course.corrected': 'corrected «{course}».',
  'home.activity.course.imported': 'loaded a version of «{course}».',
  'home.activity.course.archived': 'archived «{course}».',
  'home.activity.course.duplicated': 'duplicated a course into «{course}».',
  'home.activity.untitledCourse': 'Untitled course',
  'home.activity.goneCourse': 'Course no longer there',
  'home.noa.title': 'Noa',
  'home.noa.body':
    'The companies that have gone past 80 % of their Noa cap this month, and those where Noa is paused. Never the amount: the operator tool gives it.',
  'home.noa.empty': 'No company has gone past 80 % of its cap or has Noa paused.',
  'home.noa.warning': 'Past 80 % of its cap this month.',
  'home.noa.paused': 'It has reached its cap: Noa is paused until next month.',
  'home.noa.updating': 'Noa is paused for an update: it needs to be turned on again.',

  'companies.title': 'Companies',
  'companies.body': "Each company's name, status and counts. The list never shows a person.",
  'companies.add': 'Add company',
  'companies.filters.label': 'Search and filter companies',
  'companies.search': 'Search companies',
  'companies.filter.status': 'Status',
  'companies.filter.allStatuses': 'All statuses',
  'companies.count': '{count, plural, one {# company} other {# companies}}',
  'companies.empty.title': 'There are no companies yet',
  'companies.empty.body': 'Add the first one with «Add company».',
  'companies.noResults': 'No companies match this search and this filter.',
  'companies.open': 'View details',
  'companies.openLabel': 'View details of {company}',
  'company.counts': 'Counts',
  'company.activePeople': 'Active people',
  'company.invitedPeople': 'Invited, not yet in',
  'company.hrPeople': 'HR team',
  'company.departments': 'Departments',
  'company.activeCourses': 'Active courses',
  'company.activeCoursesHint':
    '«Active courses» counts those HR has already launched; one just activated stays «Not launched».',
  'company.back': 'All companies',
  'company.audited':
    "Opening this page is recorded in the company's audit trail, as are editing it, viewing its HR team, sending them an invitation again, suspending it, reactivating it and activating a course for it.",
  'company.notFound.title': 'This company does not exist or is no longer available',
  'company.notFound.body': 'It may have been offboarded. Go back to the list of companies.',
  'company.reopen': 'Open this company again',
  'company.courses.title': 'Eklevo courses activated',
  'company.courses.empty': 'It has no Eklevo course activated yet.',
  'company.courses.toCatalog': 'Activate a course from «Courses»',
  'company.noa.title': 'Noa',
  'company.noa.state.on': 'On',
  'company.noa.state.off': 'Off',
  'company.noa.state.updating': 'Paused for an update',
  'company.noa.state.paused': 'Paused at its cap',
  'company.noa.on': "Noa answers the company's people.",
  'company.noa.off':
    "Noa is not turned on in this company. Eklevo's team turns it on with the operator tool, after the specialist review.",
  'company.noa.updating':
    'Noa now runs with versions other than the ones validated when it was turned on in this company. Until it is turned on again with the new ones, once the trap texts pass on staging, only the crisis filter answers.',
  'company.noa.paused':
    'It has reached its cap for this month: until next month, only the crisis filter answers.',
  'company.noa.warning': 'Past 80 % of its cap this month.',
  'company.noa.validated': 'Validated with',
  'company.noa.running': 'Running versions',
  'company.noa.prompt': 'Prompt',
  'company.noa.crisisTerms': 'Crisis list',
  'company.noa.model': 'Model',
  'company.noa.validatedAt': 'Turned on',
  'company.noa.at': '{at, date, medium}, {at, time, short}',
  'company.noa.unknown': 'Unknown',
  'company.noa.amount': 'Only the operator tool gives the exact cap and amount.',
  'company.passkeyNote': 'We will ask for your passkey to confirm it.',
  'company.suspend': 'Suspend',
  'company.suspend.title': 'Suspend {company}?',
  'company.suspend.body':
    'Everyone at the company loses access to Eklevo right now, to the portal and the HR console, including anyone signed in, and sign-in links already sent stop working. Until you reactivate it. Its data does not change.',
  'company.suspend.busy': 'Suspending…',
  'company.suspended': 'Company suspended: nobody from the company can sign in.',
  'company.reactivate': 'Reactivate',
  'company.reactivate.title': 'Reactivate {company}?',
  'company.reactivate.body':
    'The people of the company will be able to sign in again with a sign-in link, with everything they had.',
  'company.reactivate.busy': 'Reactivating…',
  'company.reactivated': 'Company reactivated: its people can sign in again.',
  'company.error.unchanged': 'Someone else on the team had already made this change.',
  'company.created':
    '{count, plural, =0 {Company created. The HR team will receive the invitation in a few minutes.} one {Company created. The HR team will receive the invitation in a few minutes, and the course waits «Not launched» until HR launches it.} other {Company created. The HR team will receive the invitation in a few minutes, and the courses wait «Not launched» until HR launches them.}}',
  'company.data.title': "Company's details",
  'company.data.locale': 'Default language',
  'company.data.timeZone': 'Time zone',
  'company.data.brand': 'Brand colours',
  'company.data.brand.none': "Eklevo's",
  'company.data.logo': 'Logo',
  'company.data.logo.alt': '{company} logo',
  'company.data.logo.none': 'No logo',
  'company.data.legalName': 'Legal name',
  'company.data.taxId': 'Tax ID (CIF/NIF)',
  'company.data.address': 'Address',
  'company.data.privacyContact': 'Privacy contact',
  'company.data.none': 'Not given',
  'company.edit': 'Edit',
  'company.edit.title': "Edit the company's details",
  'company.edit.intro':
    "The changes show from now on in the company's portal and HR console. The name and the time zone also count in the emails sent and the certificates downloaded from now on.",
  'company.edit.legal.hint': 'Optional. Leave one blank to remove it.',
  'company.edit.save': 'Save changes',
  'company.edit.busy': 'Saving…',
  'company.edit.saved': "Company's details saved.",
  'company.edit.purpose': 'Save the changes of {company}.',
  'company.edit.unchanged': 'There is no change to save.',
  'company.edit.problems': 'Check the fields marked.',
  'company.edit.logo.kept': 'The company has a logo.',
  'company.edit.logo.removed': 'The logo will be removed on saving.',
  'company.edit.logo.undo': 'Keep the logo',
  'company.edit.noticeMissing':
    "The latest version of this company's information notice is not in this language. First publish a version that includes it, with the operator tool («org notice»), and then change it again.",
  'company.edit.stale':
    "Someone else saved changes to the company's details while you were editing them, and yours were not saved. These are the current details: choose «Edit» again to change them.",
  'company.hr.title': 'HR team',
  'company.hr.body':
    "Only the people with the HR role, invited or active: the only place of the panel with names and emails. Each time it is shown is recorded in the company's audit trail.",
  'company.hr.empty': 'The company has no HR person invited or active.',
  'company.hr.status.active': 'Active',
  'company.hr.status.invited': 'Invited',
  'company.hr.invitation.usable': 'Invitation sent on {at, date, medium}',
  'company.hr.invitation.expired': 'The last invitation, of {at, date, medium}, no longer works',
  'company.hr.invitation.none': 'No invitation sent yet',
  'company.hr.resend': 'Send the invitation again',
  'company.hr.send': 'Send the invitation',
  'company.hr.resend.busy': 'Sending…',
  'company.hr.confirm.resend':
    'They will receive an email with a new link to sign in, and the link of the previous invitation will stop working. At most 3 a day per person.',
  'company.hr.confirm.send':
    'They will receive an email with a link to sign in. At most 3 a day per person.',
  'company.hr.resent':
    'Invitation sent. It will arrive in a few minutes, and the previous one no longer works.',
  'company.hr.noNotice':
    'The company has no privacy notice yet: no invitation goes out until it has one.',
  'company.hr.error.limit':
    'This person has already received 3 invitations today. Try again tomorrow.',
  'company.hr.error.notInvitable': 'This person has already signed in: they need no invitation.',
  'company.hr.error.notFound': "This person is no longer in the company's HR team.",
  'company.hr.suspended': 'The company is suspended: reactivate it to send invitations.',
  'catalog.title': 'Courses',
  'catalog.body':
    "Eklevo's catalogue. A published course is activated company by company, and stays «Not launched» until HR launches it.",
  'catalog.filters.label': 'Search and filter courses',
  'catalog.search': 'Search courses',
  'catalog.filter.level': 'Level',
  'catalog.filter.allLevels': 'All levels',
  'catalog.empty.title': 'There are no courses in the catalogue yet',
  'catalog.empty.body':
    'Create the first one with “Create a new course”, or load it with the operator tool.',
  'catalog.noResults': 'No courses match this search and this filter.',
  'catalog.untitled': 'Untitled course',
  'catalog.level': 'Level {level}',
  'catalog.lessons': '{count, plural, one {# lesson} other {# lessons}}',
  'catalog.duration': '{minutes, number} min',
  'catalog.activeIn':
    '{count, plural, =0 {Not active in any company} one {Active in # company} other {Active in # companies}}',
  'catalog.activeInHint':
    'Counts the companies where HR has already launched it, suspended ones included.',
  'catalog.activate': 'Activate for a company',
  'catalog.activate.label': 'Activate for a company: {course}',
  'catalog.activate.company': 'Company',
  'catalog.activate.choose': 'Choose a company',
  'catalog.activate.suspended': '{company} (suspended)',
  'catalog.activate.hint':
    "It will stay «Not launched»: the company's HR decides when to launch it.",
  'catalog.activate.submit': 'Activate',
  'catalog.activate.busy': 'Activating…',
  'catalog.activate.required': 'Choose a company.',
  'catalog.activate.done': 'Activated for {company}. It stays «Not launched» until HR launches it.',
  'catalog.activate.already': '{company} already has this course.',
  'catalog.activate.purpose': 'Activate «{course}» for {company}.',
  'catalog.activate.unavailable':
    'This course can no longer be activated, or the company is no longer available.',
  'catalog.activate.noCompanies': 'There are no companies to activate it for yet.',
  'catalog.create': 'Create a new course',
  'catalog.edit': 'Edit',
  'catalog.edit.label': 'Edit “{course}”',
  'catalog.duplicate': 'Duplicate',
  'catalog.duplicate.label': 'Duplicate “{course}”',
  'catalog.duplicate.title': 'Duplicate “{course}”',
  'catalog.duplicate.body.published':
    'We will create a new draft with the whole content of the published version: lessons, assessments, materials and videos. The original course does not change.',
  'catalog.duplicate.body.draft':
    'We will create a new draft with the whole content of this draft: lessons, assessments, materials and videos. The original course does not change.',
  'catalog.duplicate.after':
    'The duplicate has no series or level and is not active in any company. We will tell you here when it is ready, so that you can review it in the editor and publish it.',
  'catalog.duplicate.busy': 'Duplicating…',
  'catalog.duplicate.unavailable': 'This course is no longer available.',
  'catalog.duplicate.running':
    'Duplicating: the files are being copied, and with big videos this can take a few minutes. You can keep working; the draft will appear in “Courses”.',
  'catalog.duplicate.done': 'Duplicated: the draft is now in “Courses”.',
  'catalog.duplicate.open': 'Open the draft',
  'catalog.duplicate.failed':
    'It could not be duplicated and no draft was created. Try again. Code: {code}.',
  'catalog.duplicate.unknown':
    'We do not know how it ended. If the duplicate does not appear in “Courses”, try again.',
  'catalog.archive': 'Archive',
  'catalog.archive.label': 'Archive “{course}”',
  'catalog.archive.checking': 'Checking…',
  'catalog.archive.title': 'Archive “{course}”',
  'catalog.archive.body':
    'No new company will be able to activate it. An archived course cannot be edited or unarchived, but it can be duplicated.',
  'catalog.archive.companies':
    '{count, plural, =0 {No company has it activated.} one {# company has it activated and will no longer be able to assign it to anyone else.} other {# companies have it activated and will no longer be able to assign it to anyone else.}}',
  'catalog.archive.people':
    '{count, plural, =0 {Nobody has it assigned and unfinished.} one {# person has it assigned and unfinished: they can finish it with their version.} other {# people have it assigned and unfinished: they can finish it with their version.}}',
  'catalog.archive.draft':
    'The draft open in the editor will be discarded, with the changes that have not been published.',
  'catalog.archive.changed':
    'The course changed while you were confirming: check again what archiving it will do.',
  'catalog.archive.busy': 'Archiving…',
  'catalog.archive.done': '“{course}” archived. Whoever had it assigned can finish it.',
  'catalog.archive.unavailable': 'This course is already archived or has never been published.',
  'editor.publish.purpose': 'Publish “{course}” in the catalogue.',
  'editor.notFound': 'This course does not exist or is no longer available.',
  'editor.back': 'Back to Courses',
  'wizard.title': 'Add a new company',
  'wizard.body':
    'The company’s details, its HR team, its information notice and the courses it starts with. What you type is kept only in this tab until you create the company.',
  'wizard.steps.label': 'Steps to add a company',
  'wizard.step.company': 'Company details',
  'wizard.step.people': 'Employees',
  'wizard.step.hr': 'HR team',
  'wizard.step.notice': 'Information notice',
  'wizard.step.courses': 'Courses',
  'wizard.step.summary': 'Summary',
  'wizard.step.position': 'Step {step} of {total}',
  'wizard.step.done': 'done',
  'wizard.step.problems': 'with details to check',
  'wizard.step.pending': 'not yet',
  'wizard.back': 'Back',
  'wizard.next': 'Next',
  'wizard.cancel': 'Cancel',
  'wizard.problems': 'Check the details marked in this step to go on.',
  'wizard.problems.server':
    'The company was not created: there are details to check in this step. Correct them and press «Create company» again.',
  'wizard.leave.title': 'Leave without creating the company?',
  'wizard.leave.body':
    'What you have typed will be lost: it is kept only in this tab until you create the company.',
  'wizard.leave.stay': 'Keep adding it',
  'wizard.leave.confirm': 'Leave without creating',
  'wizard.locale.es': 'Spanish',
  'wizard.locale.ca': 'Catalan',
  'wizard.locale.en': 'English',
  'wizard.timeZone.Europe/Madrid': 'Mainland Spain and Balearic Islands (Europe/Madrid)',
  'wizard.timeZone.Atlantic/Canary': 'Canary Islands (Atlantic/Canary)',
  'wizard.company.intro':
    'The name its people will see, its language, its time zone and, if you like, its legal details and its colours.',
  'wizard.company.name': 'Company name',
  'wizard.company.name.hint': 'It cannot be the same as another Eklevo company’s.',
  'wizard.company.locale': 'Default language',
  'wizard.company.locale.hint':
    'The language of the invitations and of new people. Each person can change their own.',
  'wizard.company.timeZone': 'Time zone',
  'wizard.company.timeZone.hint':
    'For the company’s due dates, reminders, certificates and reports.',
  'wizard.company.legal': 'Legal details',
  'wizard.company.legal.hint': 'Optional: they can also be filled in later, from «Edit».',
  'wizard.company.legalName': 'Legal name',
  'wizard.company.legalName.hint':
    'The registered name of the company, if it is not the same as its name.',
  'wizard.company.taxId': 'Tax ID (CIF/NIF)',
  'wizard.company.taxId.hint':
    'A Spanish CIF, NIF or NIE, or an intra-community VAT number. A Spanish one is stored without “ES”.',
  'wizard.company.address': 'Address',
  'wizard.company.address.hint': 'The address of its head office, on one line.',
  'wizard.company.privacyContact': 'Privacy contact',
  'wizard.company.privacyContact.hint':
    'The email address its people can write to about their data. A shared mailbox, such as privacy@ or dpo@, is better than a person’s.',
  'wizard.logo': 'Company logo',
  'wizard.logo.hint':
    "PNG or JPEG, up to 1 MB and between 32 and 4,000 pixels a side. Optional: without a logo, the portal and the console show Eklevo's.",
  'wizard.logo.choose': 'Choose logo',
  'wizard.logo.ready': 'Logo ready: {file}',
  'wizard.logo.change': 'Change logo',
  'wizard.logo.remove': 'Remove logo',
  'upload.sending': 'Uploading {file}',
  'upload.percent': '{percent, number, percent}',
  'upload.progress': 'Upload progress',
  'upload.scanning': 'Analysing {file}: we check that the file is safe.',
  'upload.cancel': 'Cancel',
  'upload.cancel.label': 'Cancel the upload of {file}',
  'wizard.brand': 'Brand colours',
  'wizard.brand.use': 'Use the company’s colours',
  'wizard.brand.use.hint':
    'Optional. Without them, the company’s portal and console use Eklevo’s. They are never shown in this panel or in emails.',
  'wizard.brand.primary': 'Primary colour',
  'wizard.brand.accent': 'Accent colour',
  'wizard.brand.pick.primary': 'Pick the primary colour',
  'wizard.brand.pick.accent': 'Pick the accent colour',
  'wizard.brand.hint': 'As #RRGGBB, such as #1b2a4a.',
  'wizard.brand.contrast': 'Contrast',
  'wizard.brand.contrast.intro':
    'Every use of the colours must be easy to read (WCAG 2.2). Colours that fail a rule cannot be saved; with a design warning, they can.',
  'wizard.brand.rule.primary-text': 'Text on the primary colour',
  'wizard.brand.rule.primary-text-muted': 'Secondary text on the primary colour',
  'wizard.brand.rule.primary-text-hover': 'Text of a primary button under the pointer',
  'wizard.brand.rule.primary-ui': 'Primary colour on the cards and the page',
  'wizard.brand.rule.accent-text': 'Text on the accent colour',
  'wizard.brand.rule.accent-text-hover': 'Text of an accent button under the pointer',
  'wizard.brand.rule.accent-ui-primary': 'Accent button on the primary colour',
  'wizard.brand.rule.accent-ui-surface': 'Accent button on a white card',
  'wizard.brand.check.passed': 'Passes: {ratio} (minimum {minimum})',
  'wizard.brand.check.failed': 'Fails: {ratio} (minimum {minimum})',
  'wizard.brand.check.warning':
    'Design warning: {ratio}, under {minimum}. It can be saved: the button reads by its text, but stands out less.',
  'wizard.people.intro':
    'The HR team loads the list of employees from its console, in «Employees and progress» › «Import CSV», with this template. That way, their details never go through Eklevo’s panel.',
  'wizard.people.columns':
    'One person per line: the email (required), the first name and surnames and, if wanted, the department, the team, the job title, the start date, the language and the role.',
  'wizard.people.invitations':
    'Employees receive nothing when the company is created: HR sends the invitations when it decides.',
  'wizard.people.template': 'Download CSV template',
  'wizard.people.templateFile': 'employees-template',
  'wizard.hr.heading': 'Designate the HR managers',
  'wizard.hr.intro':
    'These people will receive an invitation email to Eklevo. Once in, they will also have the HR console.',
  'wizard.hr.person': 'Person {count}',
  'wizard.hr.firstName': 'First name',
  'wizard.hr.lastName': 'Surnames',
  'wizard.hr.email': 'Work email',
  'wizard.hr.email.hint':
    'One person’s own address, never a shared mailbox such as hr@ or info@: each HR action is recorded under the name of whoever takes it.',
  'wizard.hr.add': 'Add another person',
  'wizard.hr.remove': 'Remove',
  'wizard.hr.removeLabel': 'Remove person {count}',
  'wizard.hr.max': 'At most 10 people here; the rest later, from the HR console.',
  'wizard.notice.intro':
    'The company’s privacy information notice: each person sees it and confirms they have read it when they first sign in, and the invitation email links to its address. Without a notice, no invitation goes out.',
  'wizard.notice.legend.es': 'Notice in Spanish',
  'wizard.notice.legend.ca': 'Notice in Catalan',
  'wizard.notice.legend.en': 'Notice in English',
  'wizard.notice.default': 'It is the company’s language: this notice is required.',
  'wizard.notice.add.es': 'Also add the notice in Spanish',
  'wizard.notice.add.ca': 'Also add the notice in Catalan',
  'wizard.notice.add.en': 'Also add the notice in English',
  'wizard.notice.format': 'Notice format',
  'wizard.notice.format.url': 'Link',
  'wizard.notice.format.text': 'Text',
  'wizard.notice.url': 'Notice address',
  'wizard.notice.url.hint': 'A page of the company’s, starting with https://.',
  'wizard.notice.body': 'Notice text',
  'wizard.notice.body.count': '{count, number}/20,000 characters, at least 20',
  'wizard.courses.intro':
    'The courses of Eklevo’s catalogue the company starts with. They are activated and «Not launched»: HR decides when to launch them and whom to assign them to.',
  'wizard.courses.list': 'Published courses of the catalogue',
  'wizard.courses.optional': 'Optional: they can also be activated later, from «Courses».',
  'wizard.courses.count':
    '{count, plural, =0 {No course selected} one {# course selected} other {# courses selected}}',
  'wizard.courses.empty':
    'There are no published courses in the catalogue yet. You can activate them later, from «Courses».',
  'wizard.summary.intro':
    'Check the details before creating the company. Everything is created at once, or nothing.',
  'wizard.summary.edit.company': 'Edit the company details',
  'wizard.summary.edit.hr': 'Edit the HR team',
  'wizard.summary.edit.notice': 'Edit the information notice',
  'wizard.summary.edit.courses': 'Edit the courses',
  'wizard.summary.edit': 'Edit',
  'wizard.summary.brand.none': 'Eklevo’s',
  'wizard.summary.logo.none': "Eklevo's",
  'wizard.summary.none': 'Not given',
  'wizard.summary.people': 'HR loads them from its console, with the template.',
  'wizard.summary.notice.url': 'Link',
  'wizard.summary.notice.text':
    '{count, plural, one {Text of # character} other {Text of # characters}}',
  'wizard.summary.courses.none': 'None: they can be activated later, from «Courses».',
  'wizard.summary.invitations':
    '{count, plural, one {Once it is created, the person of the HR team will receive the invitation.} other {Once it is created, the # people of the HR team will receive the invitation.}}',
  'wizard.create': 'Create company',
  'wizard.create.busy': 'Creating the company…',
  'wizard.create.purpose': 'Create the company {company}.',
  'wizard.error.required': 'This is missing.',
  'wizard.error.field': 'Check this.',
  'wizard.error.choice': 'Choose one of the options.',
  'wizard.error.name.too-short': 'Write at least 2 characters.',
  'wizard.error.name.too-long': 'At most 120 characters.',
  'wizard.error.name.taken':
    'There is already a company with this name, also among the offboarded ones. Choose another.',
  'wizard.error.line.invalid-characters':
    'It has line breaks or invisible characters that cannot be used.',
  'wizard.error.color.invalid': 'Write a colour as #RRGGBB, such as #1b2a4a.',
  'wizard.error.color.contrast':
    'With the other colour, it fails a contrast rule: see the list below.',
  'wizard.error.person.too-long': 'At most 100 characters.',
  'wizard.error.email.invalid': 'Write a valid email.',
  'wizard.error.email.generic-mailbox':
    'It is a shared mailbox. Write the email of one specific person.',
  'wizard.error.email.duplicate': 'This email is already in the team.',
  'wizard.error.legalName.too-long': 'At most 200 characters.',
  'wizard.error.address.too-long': 'At most 300 characters.',
  'wizard.error.taxId.invalid': 'It is not in the format of a CIF, NIF, NIE or VAT number.',
  'wizard.error.taxId.taken':
    'There is already a company with this tax ID, offboarded ones included.',
  'wizard.error.hr.required': 'Add at least one person.',
  'wizard.error.hr.too-many': 'At most 10 people. The rest are added later, from the HR console.',
  'wizard.error.notice.missing': 'The notice in the company’s language is missing.',
  'wizard.error.notice.url': 'Write an address starting with https://.',
  'wizard.error.notice.too-short': 'Write at least 20 characters.',
  'wizard.error.notice.too-long': 'At most 20,000 characters.',
  'wizard.error.notice.invalid-characters': 'It has invisible characters that cannot be used.',
  'wizard.error.notice.placeholder':
    'This text still has placeholders of the model to fill in: {placeholders}.',
  'wizard.error.courses.too-many': 'At most 50 courses.',
  'wizard.error.course.not-available':
    'This course can no longer be activated: remove it to create the company.',
  'wizard.error.logo.uploading': "Wait for the logo's upload to finish, or cancel it.",
  'wizard.error.logo.scanning': 'The logo is still being checked. Wait a moment and try again.',
  'wizard.error.logo.too-large': 'The logo is larger than 1 MB.',
  'wizard.error.logo.empty': 'The file is empty.',
  'wizard.error.logo.wrong-type': 'The logo must be a PNG or JPEG image.',
  'wizard.error.logo.wrong-dimensions': 'The logo must be between 32 and 4,000 pixels a side.',
  'wizard.error.logo.infected':
    'The security check found a problem in the file, and it was not uploaded.',
  'wizard.error.logo.scan-failed': 'The file could not be checked. Try another one.',
  'wizard.error.logo.missing': 'The file did not arrive whole. Upload it again.',
  'wizard.error.logo.expired': "The logo's upload has expired. Upload it again.",
  'wizard.error.logo.network': 'The connection was lost while uploading. Upload it again.',
  'wizard.error.logo.scan-timeout': 'Checking the file is taking too long. Upload it again later.',
  'wizard.error.logo.failed': 'The logo could not be uploaded. Try again.',
  'wizard.error.rateLimited':
    'Too many attempts to create companies. Wait a while and try again: what you have typed stays here as long as you keep the tab open.',
  'wizard.error.generic':
    'The company could not be created. Try again in a few minutes: what you have typed is still here.',
  'reauth.title': 'Confirm it is you',
  'reauth.body': 'This action needs your passkey: your device’s fingerprint, face or PIN.',
  'reauth.confirm': 'Use my passkey',
  'reauth.busy': 'Waiting for your passkey…',
  'idle.title': 'Are you still there?',
  'idle.body':
    'For security, the session closes after 30 minutes without activity. It will close at {at, time, short}.',
  'idle.stay': 'Stay signed in',
  'ending.notice':
    'Your session ends at {endsAt, time, short}, when it reaches 8 hours. You will then need to sign in again.',
} as const satisfies BackOfficeCatalog
